/**
 * Relação do exemplar com o lote (migration 065).
 *
 * A responsabilidade é DERIVADA da relação e serve de orientação: a regra
 * definitiva de quem responde pelo manejo cabe à AMMA confirmar.
 */

export const LOT_RELATION_LABELS: Record<string, string> = {
  CALCADA: "Calçada",
  INTERIOR_IMOVEL: "Interior do imóvel",
  AREA_PUBLICA: "Área pública",
  AREA_VERDE: "Área verde",
  PARQUE: "Parque",
  DIVISA_LOTE: "Divisa de lote",
  CANTEIRO_CENTRAL: "Canteiro central",
  LEITO_VIARIO: "Leito viário",
  INDETERMINADO: "Indeterminado",
};

export const LOT_RELATION_OPTIONS = [
  "CALCADA",
  "INTERIOR_IMOVEL",
  "DIVISA_LOTE",
  "AREA_PUBLICA",
  "AREA_VERDE",
  "PARQUE",
  "CANTEIRO_CENTRAL",
  "LEITO_VIARIO",
] as const;

export const LOT_RELATION_RESPONSIBILITY: Record<string, string> = {
  CALCADA: "Poder público",
  AREA_PUBLICA: "Poder público",
  AREA_VERDE: "Poder público",
  PARQUE: "Poder público",
  CANTEIRO_CENTRAL: "Poder público",
  LEITO_VIARIO: "Poder público",
  INTERIOR_IMOVEL: "Proprietário do imóvel",
  DIVISA_LOTE: "Proprietários dos lotes vizinhos",
  INDETERMINADO: "A confirmar",
};

export const LOT_RELATION_METHOD_LABELS: Record<string, string> = {
  CONFIRMADO_CAMPO: "Confirmado em campo",
  INFORMADO_ESCRITORIO: "Informado no escritório",
};

export const RESPONSIBILITY_NOTE = "Responsabilidade derivada da relação com o lote; regra a confirmar com a AMMA.";

export function lotRelationLabel(code: string | null | undefined): string {
  if (!code) return "—";
  return LOT_RELATION_LABELS[code] ?? code;
}

export interface LotReferenceView {
  /** "Lote que contém o exemplar" ou "Lote em frente". Nulo = sem lote de referência. */
  title: string | null;
  block: string;
  lot: string;
  registration: string;
  adjacent: string | null;
  street: string;
  cep: string;
  /** Explica de onde veio o CEP, ou por que ficou em aberto. */
  cepNote: string | null;
}

interface LotReferenceInput {
  kind?: string | null;
  lot_number?: string | null;
  lot_registration?: string | null;
  block_number?: string | null;
  block_code?: string | null;
  adjacent_lot_number?: string | null;
  adjacent_block_number?: string | null;
  street?: string | null;
  cep?: string | null;
  cep_status?: string | null;
  cep_candidates?: string[];
}

const CEP_NOTES: Record<string, string> = {
  UNICO_NO_BAIRRO: "Único CEP do logradouro neste bairro",
  UNICO_DO_LOGRADOURO: "Único CEP do logradouro",
  MAIS_PROXIMO: "CEP mais próximo do logradouro",
  NAO_ENCONTRADO: "Logradouro sem CEP na base",
  SEM_LOGRADOURO: "Sem logradouro identificado",
};

/** Mesmo texto no card, no PDF e no cadastro (FIQ-1). */
export function lotReferenceView(
  ref: LotReferenceInput | null | undefined,
  stored?: { lot_number?: string | null; lot_registration?: string | null; block_number?: string | null;
             block_code?: string | null; postal_code?: string | null; reference_kind?: string | null },
): LotReferenceView {
  const r = ref ?? {};
  const kind = stored?.reference_kind ?? r.kind ?? null;
  const lot = stored?.lot_number ?? r.lot_number ?? null;
  const blockNumber = stored?.block_number ?? r.block_number ?? null;
  const blockCode = stored?.block_code ?? r.block_code ?? null;
  const cep = stored?.postal_code ?? r.cep ?? null;
  let cepNote = r.cep_status ? CEP_NOTES[r.cep_status] ?? null : null;
  if (!cep && r.cep_status === "AMBIGUO") {
    cepNote = `Logradouro com vários CEPs (${(r.cep_candidates ?? []).join(", ")}): confirme pela numeração`;
  }
  return {
    title: kind === "CONTIDO" ? "Lote que contém o exemplar" : kind === "EM_FRENTE" ? "Lote em frente ao exemplar" : null,
    block: blockNumber ? `${blockNumber}${blockCode ? ` (${blockCode})` : ""}` : "—",
    lot: lot ?? "—",
    registration: stored?.lot_registration ?? r.lot_registration ?? "—",
    adjacent: r.adjacent_lot_number
      ? `${r.adjacent_lot_number}${r.adjacent_block_number ? `, quadra ${r.adjacent_block_number}` : ""}`
      : null,
    street: r.street ?? "—",
    cep: cep ?? "—",
    cepNote,
  };
}
